// useDocMeta — vanilla per-page <title>, <meta>, OG, JSON-LD without adding
// react-helmet as a dependency. Phase 1 / Phase 5 SEO foundation.
//
// Usage:
//   useDocMeta({
//     title: "How It Works · GovBidIQ",
//     description: "...",
//     path: "/how-it-works",
//     image: "/og/how-it-works.png",     // optional
//     jsonLd: { ... }                     // optional schema.org object
//   });

import { useEffect } from "react";

const SITE = "GovBidIQ";
const DEFAULT_DESC = "AI-powered government contract intelligence — bid/no-bid decisions, prospect scoring, opportunity reports.";
const DEFAULT_IMG = "/og-default.png";

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, obj) {
  if (!obj) return;
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export function useDocMeta({ title, description, path, image, jsonLd } = {}) {
  useEffect(() => {
    const desc = description || DEFAULT_DESC;
    const img = image || DEFAULT_IMG;
    const url = (typeof window !== "undefined" ? window.location.origin : "") + (path || "");
    const fullTitle = title ? `${title}` : SITE;

    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    if (path) upsertLink("canonical", url);
    if (jsonLd) upsertJsonLd(path || "default", jsonLd);
  }, [title, description, path, image, jsonLd]);
}
